import type { OpenFeatureSupportedFlags } from '../../openfeature/types';
import type { FlagKey } from './types';

export const flags = {
  'pricing-social-proof': {
    enabled: [true, false],
  },
  'is-education-plans-routing': {
    enabled: [true, false],
  },
  'live-editor-csat': {
    enabled: [true, false],
  },
  'live-linear-navigation': {
    enabled: [true, false],
  },
  'nux-editor-checklist': {
    enabled: [true, false],
  },
  'nux-forceful-editor-tour': {
    enabled: [true, false],
  },
  'nux-mobile-intent-onboarding': {
    enabled: [true, false],
  },
  'nux-backfilling-onboarding-data': {
    enabled: [true, false],
  },
  'ins-results-ai-summary': {
    enabled: [true, false],
  },
  'ins-reset-results-v3': {
    enabled: [true, false],
  },
  'ins-call-new-endpoints': {
    enabled: ['internal', 'dev', 'disabled'],
  },
  'test-error-tracking': {
    enabled: [true, false],
  },
  'ai-menti-builder': {
    version: ['v1', 'v2', 'v3', 'conv-ui-v1', 'off'],
  },
  'bees-feedback-loop': {
    enabled: [true, false],
  },
  'bees-actionable-feedback': {
    version: [
      'stars_only',
      'stars_and_structured_feedback',
      'stars_and_unstructured_feedback',
      'stars_and_random_feedback',
      'off',
    ],
  },
  'bees-business-remodel': {
    enabled: [true, false],
  },
  'og-homepage-personalized-header': {
    enabled: [true, false],
  },
  'og-google-signup': {
    version: ['v1', 'v2'],
  },
  'mangos-find-and-join-workspace': {
    enabled: [true, false],
  },
  'mangos-confirm-workspace-invite-full-page': {
    enabled: [true, false],
  },
  'mango-nudge-2-join': {
    enabled: [true, false],
  },
  'mangos-nux-workspace-intent-survey': {
    enabled: [true, false],
  },
  'cx-hide-deprecated-slide-types': {
    enabled: [true, false],
  },
  'cx-ai-editing': {
    enabled: [true, false],
  },
  'cx-pyne-onboarding': {
    enabled: [true, false],
  },
  'pay-discount-badges-cbc': {
    enabled: [true, false],
  },
  'pay-upsell-toggles-cbc': {
    enabled: [true, false],
  },
  'pay-purchase-to-upgrade': {
    enabled: [true, false],
  },
  'biz-promote-enterprise-banner': {
    enabled: [true, false],
  },
  'pug-onboarding-plans-page-aa': {
    enabled: [true, false],
  },
  'pug-onboarding-remove-basic-plan': {
    enabled: ['off', 'v1'],
  },
  'pug-paid-value-modal': {
    enabled: [true, false],
  },
  'pug-paid-value-modal-user-menu': {
    enabled: [true, false],
  },
  'pug-value-oriented-pricing-page': {
    enabled: [true, false],
  },
  'pug-test-server-side-rendering': {
    heading: ['Real heading', 'Fake heading'],
    font_size: [1000, 0],
    enabled: [true, false],
  },
  'sli-new-look-dots': {
    enabled: [true, false],
  },
  'sli-new-look-word-cloud': {
    enabled: [true, false],
  },
  'sli-new-look-pin-on-image': {
    enabled: [true, false],
  },
  'sli-new-look-hundred-points': {
    enabled: [true, false],
  },
  'sli-new-look-rating': {
    enabled: [true, false],
  },
  'ai-conv-ui': {
    version: ['v1', 'feature-at-capacity', 'off'],
  },
  'ai-conv-ui-intro': {
    version: ['off', 'v1-skip-intro-a', 'v1-skip-intro-b'],
  },
} as const satisfies OpenFeatureSupportedFlags;

export const flagsMetadata: Record<
  FlagKey,
  { description: string; displayName: string }
> = {
  'pricing-social-proof.enabled': {
    displayName: 'PUG011 - Social proof on plans page',
    description: 'Show client logos on the pricing page',
  },
  'is-education-plans-routing.enabled': {
    displayName: 'PAY02 - Education plans routing',
    description:
      'Route to education plans when knowing that the user is an educational user',
  },
  'live-editor-csat.enabled': {
    displayName: 'LIVE - Show CSAT Form',
    description: 'Show the CSAT form to the user in the editor.',
  },
  'live-linear-navigation.enabled': {
    displayName: 'LIVE - Linear Navigation',
    description: 'Show new linear navigation features',
  },
  'nux-editor-checklist.enabled': {
    displayName: 'NUX - Editor Checklist',
    description: 'Show a checklist in the editor',
  },
  'nux-forceful-editor-tour.enabled': {
    displayName: 'NUX - Forceful Editor Tour',
    description: 'Show a forceful tour in the editor',
  },
  'nux-mobile-intent-onboarding.enabled': {
    displayName: 'NUX - Mobile Intent Onboarding',
    description: 'Enable mobile intent onboarding for new users',
  },
  'nux-backfilling-onboarding-data.enabled': {
    displayName: 'NUX - Backfilling onboarding data',
    description: 'Backfill onboarding data in the dashboard for existing users',
  },
  'ins-results-ai-summary.enabled': {
    displayName: 'INS - Results AI Summary',
    description: 'Show the AI summary in the results page',
  },
  'ins-reset-results-v3.enabled': {
    displayName: 'INS - Updated Reset Results Modal',
    description: 'In presentation view and editor',
  },
  'ins-call-new-endpoints.enabled': {
    displayName: 'INS - Call new endpoints',
    description: 'Call the new endpoints for results page',
  },
  'test-error-tracking.enabled': {
    displayName: 'INS - Test error tracking',
    description: 'Test error tracking',
  },
  'ai-menti-builder.version': {
    displayName: 'AI001 - AI Menti Builder',
    description: 'Show the AI Menti Builder button in the dashboard',
  },
  'bees-feedback-loop.enabled': {
    displayName: '🐝 BEES - ⭐💬 Participant feedback',
    description: 'Show feedback on the end screen',
  },
  'bees-actionable-feedback.version': {
    displayName: '🐝💬 BEES - Participant experiment with different feedback',
    description: 'Experiment with feedback on the end screen',
  },
  'bees-business-remodel.enabled': {
    displayName: '🐝🕴 BEES - Business Remodel',
    description: 'Business remodel related changes',
  },
  'og-homepage-personalized-header.enabled': {
    displayName: 'OG - Homepage Personalized Header',
    description: 'Show a personalized header on the homepage for executives',
  },
  'og-google-signup.version': {
    displayName: 'OG - Downprioritize Google Signup',
    description: 'Downprioritize Google Signup experiment in the signup flow',
  },
  'mangos-find-and-join-workspace.enabled': {
    displayName: 'MANGOS - Find and join workspace',
    description:
      'Allow new users to find and join a workspace in the onboarding process',
  },
  'mangos-confirm-workspace-invite-full-page.enabled': {
    displayName: 'MANGOS - Confirm workspace invite full page',
    description: 'Show a full page confirmation when a user joins a workspace',
  },
  'mango-nudge-2-join.enabled': {
    displayName: 'MANGO - Nudge to join',
    description: 'Displays nudge to user to click Join Existing Workspace',
  },
  'mangos-nux-workspace-intent-survey.enabled': {
    displayName: 'MANGOS <> NUX - Workspace intent survey',
    description: 'Show a survey to the user to understand their intent',
  },
  'cx-hide-deprecated-slide-types.enabled': {
    displayName: 'CX - Hide deprecated slide types',
    description: 'Hide deprecated slide types in the add slide popover',
  },
  'cx-ai-editing.enabled': {
    displayName: 'CX - AI Editing button',
    description: 'Enable AI editing button in the interactive canvas toolbar',
  },
  'cx-pyne-onboarding.enabled': {
    displayName: 'CX - Pyne.ai onboarding',
    description: 'Enable AI onboarding through Pyne.ai',
  },
  'pay-discount-badges-cbc.enabled': {
    displayName: 'PAY - Discount Badges in CBC',
    description: 'Show discount badges in the custom built checkout',
  },
  'pay-upsell-toggles-cbc.enabled': {
    displayName: 'PAY - Upsell toggles in CBC',
    description: 'Show upsell toggles in the custom built checkout',
  },
  'pay-purchase-to-upgrade.enabled': {
    displayName: 'PAY - Purchase to upgrade',
    description:
      'A flow for purchasing a new license and upgrade to member in one click',
  },
  'biz-promote-enterprise-banner.enabled': {
    displayName: 'BIZ - Promote Enterprise Banner',
    description:
      'Show the enterprise banner in the dashboard for organization leaders',
  },
  'pug-onboarding-plans-page-aa.enabled': {
    displayName: 'PUG - Onboarding plans page A/B test',
    description: 'Show a new onboarding plans page',
  },
  'pug-onboarding-remove-basic-plan.enabled': {
    displayName: 'PUG - Onboarding remove basic plan',
    description: 'Remove the basic plan from the onboarding process',
  },
  'pug-paid-value-modal.enabled': {
    displayName: 'PUG - Paid value modal',
    description:
      'Show a modal in the dashboard highlighting paid features after purchase',
  },
  'pug-paid-value-modal-user-menu.enabled': {
    displayName: 'PUG - Paid value modal in user menu',
    description:
      'Show a button in the user menu for paid users where they can trigger the paid value modal',
  },
  'pug-value-oriented-pricing-page.enabled': {
    displayName: 'PUG - Value-oriented pricing page',
    description: 'Show a value-oriented pricing page',
  },
  'pug-test-server-side-rendering.heading': {
    displayName: 'PUG - Test server side rendering',
    description: 'Test server side rendering',
  },
  'pug-test-server-side-rendering.font_size': {
    displayName: 'PUG - Test server side rendering',
    description: 'Test server side rendering',
  },
  'pug-test-server-side-rendering.enabled': {
    displayName: 'PUG - Test server side rendering',
    description: 'Test server side rendering',
  },
  'sli-new-look-dots.enabled': {
    displayName: 'Slides - New look for Dots',
    description: 'New look for the Dots visualization',
  },
  'sli-new-look-word-cloud.enabled': {
    displayName: 'Slides - New look for WordCloud',
    description: 'New look for the WordCloud visualization',
  },
  'sli-new-look-pin-on-image.enabled': {
    displayName: 'Slides - New look for PinOnImage',
    description: 'New look for the Pin on Image visualization',
  },
  'sli-new-look-hundred-points.enabled': {
    displayName: 'Slides - New look for 100 Points',
    description: 'New look for the 100 Points visualization',
  },
  'sli-new-look-rating.enabled': {
    displayName: 'Slides - New look for rating grid',
    description: 'New look for the rating grid visualization',
  },
  'ai-conv-ui.version': {
    displayName: 'AI - Conversational UI',
    description: 'Show the conversational UI in the dashboard',
  },
  'ai-conv-ui-intro.version': {
    displayName: 'AI - Conversational UI Intro',
    description: 'Whether to show the intro page in the conversational UI',
  },
};
